import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Alert as MuiAlert,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  Error,
  CheckCircle,
  Person,
  LocationOn,
  Phone,
  Assignment,
  Email,
  Close,
  Search,
  CalendarToday,
  Image,
} from '@mui/icons-material';
import api from '../services/api';
import { format } from 'date-fns';

// Driver backend URL for loading issue images
const DRIVER_BACKEND_URL = window.location.hostname === 'admin.binsandbags.co.uk'
  ? 'https://api.binsandbags.co.uk'
  : 'http://localhost:8889';

interface CollectionIssue {
  id: number;
  collection_event_id: number;
  issue_type: 'access_issue' | 'bin_damaged' | 'customer_issue' | 'vehicle_issue' | 'safety_concern' | 'other';
  status: 'new' | 'acknowledged' | 'resolved';
  title: string;
  description: string;
  image_path?: string;
  reported_at: string;
  reported_by_driver_id: number;
  acknowledged_at?: string;
  acknowledged_by?: number;
  resolved_at?: string;
  resolved_by?: number;
  resolution_notes?: string;
  // From joined data
  order_id: number;
  order_number: string;
  customer_name: string;
  delivery_address: string;
  delivery_postcode: string;
  phone_number?: string;
  driver_name: string;
  driver_code?: string;
  acknowledged_by_name?: string;
  resolved_by_name?: string;
}

const Alerts: React.FC = () => {
  const [issues, setIssues] = useState<CollectionIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState<CollectionIssue | null>(null);
  const [resolutionDialog, setResolutionDialog] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'new' | 'acknowledged' | 'resolved'>('all');
  const [orderDetailsModal, setOrderDetailsModal] = useState<{ open: boolean; orderId: number | null }>({ open: false, orderId: null });
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [collectionEvents, setCollectionEvents] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [filterDate, setFilterDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [imageModal, setImageModal] = useState<{ open: boolean; imageUrl: string | null }>({ open: false, imageUrl: null });

  useEffect(() => {
    fetchIssues();
    // Poll for new issues every 30 seconds
    const interval = setInterval(fetchIssues, 30000);
    return () => clearInterval(interval);
  }, [filterDate, filterStatus]);

  const fetchIssues = async () => {
    try {
      const params: any = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (filterDate) {
        params.date = filterDate;
      }
      const response = await api.get('/collection-issues', { params });
      setIssues(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching collection issues:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'error';
      case 'acknowledged':
        return 'warning';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleAcknowledge = async (issueId: number) => {
    try {
      await api.patch(`/collection-issues/${issueId}/acknowledge`);
      await fetchIssues(); // Refresh the list
    } catch (error) {
      console.error('Error acknowledging issue:', error);
    }
  };

  const handleResolve = async () => {
    if (!selectedIssue) return;
    
    try {
      await api.patch(`/collection-issues/${selectedIssue.id}/resolve`, {
        resolution_notes: resolutionNote
      });
      
      setResolutionDialog(false);
      setSelectedIssue(null);
      setResolutionNote('');
      await fetchIssues(); // Refresh the list
    } catch (error) {
      console.error('Error resolving issue:', error);
    }
  };

  const handleViewOrderDetails = async (orderId: number) => {
    setLoadingDetails(true);
    setOrderDetailsModal({ open: true, orderId: orderId });
    setCollectionEvents([]);
    
    try {
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get(`/collection-events/order/${orderId}`)
      ]);
      setOrderDetails(orderResponse.data);
      setCollectionEvents(eventsResponse.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setOrderDetailsModal({ open: false, orderId: null });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsModal({ open: false, orderId: null });
    setOrderDetails(null);
    setCollectionEvents([]);
  };

  const getBinColours = (order: any) => {
    if (!order) return [];
    const colours: string[] = [];
    
    ['bin_colours', 'bags_colours', 'large_bin_colours', 'first_date_bin_colours'].forEach(field => {
      if (order[field]) {
        try {
          const parsed = typeof order[field] === 'string' ? JSON.parse(order[field]) : order[field];
          if (Array.isArray(parsed)) {
            colours.push(...parsed);
          }
        } catch (e) {
          console.error(`Error parsing ${field}:`, e);
        }
      }
    });
    
    return Array.from(new Set(colours));
  };

  const filteredIssues = issues.filter(issue => {
    const statusMatch = filterStatus === 'all' || issue.status === filterStatus;
    const customerMatch = !customerSearch || 
      issue.customer_name?.toLowerCase().includes(customerSearch.toLowerCase());
    return statusMatch && customerMatch;
  });

  const newIssuesCount = issues.filter(i => i.status === 'new').length;
  const acknowledgedCount = issues.filter(i => i.status === 'acknowledged').length;
  const resolvedCount = issues.filter(i => i.status === 'resolved').length;

  if (loading) {
    return <Typography>Loading alerts...</Typography>;
  }

  return (
    <Box>
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">Driver Issue Reports</Typography>
          <Button 
            variant="contained" 
            sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
            onClick={fetchIssues}
          >
            Refresh
          </Button>
        </Box>
        
        {/* Filters Row */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            type="date"
            label="Date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          
          <TextField
            size="small"
            placeholder="Search customer name..."
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter Status</InputLabel>
            <Select
              value={filterStatus}
              label="Filter Status"
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="acknowledged">Acknowledged</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
          
          {(filterDate !== format(new Date(), 'yyyy-MM-dd') || customerSearch || filterStatus !== 'all') && (
            <Button 
              size="small"
              onClick={() => {
                setFilterDate(format(new Date(), 'yyyy-MM-dd'));
                setCustomerSearch('');
                setFilterStatus('all');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Box>

      {/* Status Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: newIssuesCount > 0 ? '#fee2e2' : '#f9fafb' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {newIssuesCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    New Issues
                  </Typography>
                </Box>
                <Error sx={{ fontSize: 40, color: '#ef4444' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: acknowledgedCount > 0 ? '#fef3c7' : '#f9fafb' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {acknowledgedCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Being Handled
                  </Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: '#f59e0b' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: '#f9fafb' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {resolvedCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Resolved Today
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: '#10b981' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Issues List */}
      {filteredIssues.length === 0 ? (
        <MuiAlert severity="success" icon={<CheckCircle />}>
          No active driver issues - All collections running smoothly
        </MuiAlert>
      ) : (
        <Grid container spacing={2}>
          {filteredIssues.map((issue) => (
            <Grid item xs={12} key={issue.id}>
              <Card sx={{ 
                borderLeft: 4, 
                borderLeftColor: 
                  issue.status === 'new' ? '#ef4444' : 
                  issue.status === 'acknowledged' ? '#f59e0b' : 
                  '#10b981'
              }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">
                        {issue.title}
                      </Typography>
                      <Chip 
                        label={issue.status.toUpperCase()} 
                        size="small" 
                        color={getStatusColor(issue.status) as any}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {format(new Date(issue.reported_at), 'MMM dd, HH:mm')}
                    </Typography>
                  </Box>

                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {issue.description}
                  </Typography>

                  {issue.image_path && (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Image />}
                        onClick={() => setImageModal({ open: true, imageUrl: issue.image_path ? `${DRIVER_BACKEND_URL}${issue.image_path}` : null })}
                      >
                        Show Image
                      </Button>
                    </Box>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="textSecondary">
                          Driver: {issue.driver_name} {issue.driver_code && `(${issue.driver_code})`}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Assignment sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="textSecondary">
                          Order: #{issue.order_number}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="textSecondary">
                          Customer: {issue.customer_name}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="textSecondary">
                          {issue.delivery_address}, {issue.delivery_postcode}
                        </Typography>
                      </Box>
                      {issue.phone_number && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            {issue.phone_number}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>

                  {issue.status === 'resolved' && issue.resolution_notes && (
                    <MuiAlert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Resolution:</strong> {issue.resolution_notes}
                      </Typography>
                      {issue.resolved_by_name && (
                        <Typography variant="caption">
                          Resolved by {issue.resolved_by_name} at {format(new Date(issue.resolved_at!), 'MMM dd, HH:mm')}
                        </Typography>
                      )}
                    </MuiAlert>
                  )}
                </CardContent>
                <CardActions>
                  {issue.status === 'new' && (
                    <Button 
                      variant="contained"
                      size="small" 
                      color="warning"
                      onClick={() => handleAcknowledge(issue.id)}
                    >
                      Acknowledge Issue
                    </Button>
                  )}
                  {issue.status === 'acknowledged' && (
                    <Button 
                      variant="contained"
                      size="small" 
                      color="success"
                      onClick={() => {
                        setSelectedIssue(issue);
                        setResolutionDialog(true);
                      }}
                    >
                      Mark as Resolved
                    </Button>
                  )}
                  <Button 
                    variant="outlined"
                    size="small"
                    onClick={() => handleViewOrderDetails(issue.order_id)}
                  >
                    View Collection Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Resolution Dialog */}
      <Dialog 
        open={resolutionDialog} 
        onClose={() => setResolutionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolve Issue</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Resolution Notes"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            placeholder="Describe how the issue was resolved..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolutionDialog(false)}>Cancel</Button>
          <Button onClick={handleResolve} variant="contained" color="success">
            Mark as Resolved
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Modal */}
      <Dialog 
        open={orderDetailsModal.open} 
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Order Details #{orderDetailsModal.orderId}
            </Typography>
            <IconButton onClick={handleCloseOrderDetails} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>Loading order details...</Typography>
            </Box>
          ) : orderDetails ? (
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer Information
                </Typography>
                <Box mb={2}>
                  <Typography variant="body1" fontWeight="medium">
                    {orderDetails.customer_first_name} {orderDetails.customer_last_name}
                  </Typography>
                  {orderDetails.customer_email && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{orderDetails.customer_email}</Typography>
                    </Box>
                  )}
                  {orderDetails.customer_phone && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{orderDetails.customer_phone}</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Collection Address */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Collection Address
                </Typography>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2">{orderDetails.delivery_address_1}</Typography>
                    {orderDetails.delivery_address_2 && (
                      <Typography variant="body2">{orderDetails.delivery_address_2}</Typography>
                    )}
                    <Typography variant="body2">
                      {orderDetails.delivery_city}, {orderDetails.delivery_postcode}
                    </Typography>
                    {orderDetails.delivery_country && (
                      <Typography variant="body2">{orderDetails.delivery_country}</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Order Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Order Information
                </Typography>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Order Date:</strong> {orderDetails.date_created ? new Date(orderDetails.date_created).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Collection Status:</strong>{' '}
                    {orderDetails.collection_status === 'collection_complete' ? (
                      <Chip label="COMPLETED" color="success" size="small" />
                    ) : orderDetails.collection_status === 'cancelled' ? (
                      <Chip label="CANCELLED" color="error" size="small" />
                    ) : (
                      <Chip label="ACTIVE" color="warning" size="small" />
                    )}
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Total Amount:</strong> £{parseFloat(orderDetails.total || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>

              {/* Collection Events */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Collection Events
                </Typography>
                {collectionEvents.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Driver</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {collectionEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              {new Date(event.scheduled_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={event.status.toUpperCase()}
                                size="small"
                                color={
                                  event.status === 'completed' ? 'success' :
                                  event.status === 'assigned' ? 'info' :
                                  event.status === 'pending' ? 'warning' : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {event.current_driver_name || event.driver_name || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No collection events found
                  </Typography>
                )}
              </Grid>

              {/* Products Ordered */}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Products Ordered
                </Typography>
                
                {/* Product names */}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {orderDetails.product_names || 'No product information available'}
                </Typography>
                
                {/* Service Details with quantities */}
                {((orderDetails.bins_per_occasion && orderDetails.bins_per_occasion > 0) ||
                  (orderDetails.bags_per_occasion && orderDetails.bags_per_occasion > 0) ||
                  (orderDetails.large_bins_per_occasion && orderDetails.large_bins_per_occasion > 0) ||
                  (orderDetails.bins_first_date && orderDetails.bins_first_date > 0) ||
                  (orderDetails.bags_first_date && orderDetails.bags_first_date > 0) ||
                  (orderDetails.large_bins_first_date && orderDetails.large_bins_first_date > 0)) && (
                  <Box mt={1}>
                    {!!orderDetails.bins_per_occasion && orderDetails.bins_per_occasion > 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Bins:</strong> {orderDetails.bins_per_occasion} per occasion × {orderDetails.bins_number_of_occasions || 1} {(orderDetails.bins_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {!!orderDetails.bags_per_occasion && orderDetails.bags_per_occasion > 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Bags:</strong> {orderDetails.bags_per_occasion} per occasion × {orderDetails.bags_number_of_occasions || 1} {(orderDetails.bags_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {!!orderDetails.large_bins_per_occasion && orderDetails.large_bins_per_occasion > 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Large Bins:</strong> {orderDetails.large_bins_per_occasion} per occasion × {orderDetails.large_bins_number_of_occasions || 1} {(orderDetails.large_bins_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {!!orderDetails.bins_first_date && orderDetails.bins_first_date > 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Free bin (first collection date only):</strong> {orderDetails.bins_first_date}
                      </Typography>
                    )}
                    {!!orderDetails.bags_first_date && orderDetails.bags_first_date > 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Free bags (first collection date only):</strong> {orderDetails.bags_first_date}
                      </Typography>
                    )}
                    {!!orderDetails.large_bins_first_date && orderDetails.large_bins_first_date > 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Free large bin (first collection date only):</strong> {orderDetails.large_bins_first_date}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* Bin Colours */}
                {(() => {
                  const binColours = getBinColours(orderDetails);
                  if (binColours.length > 0) {
                    return (
                      <Box mt={2}>
                        <Typography variant="body2">
                          <strong>Bin Colours:</strong> {binColours.join(', ')}
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Grid>

              {/* Customer Notes */}
              {orderDetails.customer_note && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Delivery Notes
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2">
                        {orderDetails.customer_note}
                      </Typography>
                    </Paper>
                  </Grid>
                </>
              )}
            </Grid>
          ) : (
            <Typography>No order details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrderDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal */}
      <Dialog 
        open={imageModal.open} 
        onClose={() => setImageModal({ open: false, imageUrl: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Issue Image</Typography>
            <IconButton 
              onClick={() => setImageModal({ open: false, imageUrl: null })} 
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {imageModal.imageUrl && (
            <Box display="flex" justifyContent="center">
              <img 
                src={imageModal.imageUrl} 
                alt="Issue attachment" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh', 
                  objectFit: 'contain'
                }} 
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Alerts;